import { Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Observable, concat } from 'rxjs';
import { defaultIfEmpty, last } from 'rxjs/operators';
import { ExpectedDeliveryService } from '../../../_services/expected-delivery.service';
import {
  ClarificationAction,
  ClarificationBadPartBins,
  ClarificationCandidate,
  ExpectedDeliveryClarification,
  ExpectedDeliveryReceipt,
} from '../../../_models/expected-delivery.interface';

/** Die Artikel-Status, die beim Neuanlegen zur Wahl stehen. */
export const ARTICLE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'NEW', label: 'Neu' },
  { value: 'USED_TESTED', label: 'Gebraucht, geprüft' },
  { value: 'USED_UNTESTED', label: 'Gebraucht, ungeprüft' },
];

/** Was mit einem Klärfall geschehen soll — gesammelt, bis am Ende gespeichert wird. */
interface ClarificationDecision {
  /** Die gewählten Artikel. Aus welcher Quelle sie stammen, steht am Kandidaten selbst. */
  articleIds: number[];
  /** Wie viel vom Rest neu angelegt wird. */
  createQuantity: number;
  /** Wie viel vom Rest ignoriert wird. */
  ignoreQuantity: number;
  /** Zustand der neu angelegten Artikel. */
  createStatus: string;
  note: string;
}

/**
 * Klärung der Positionen, die bei einer internen Lieferung nicht umgebucht
 * werden konnten.
 *
 * Führt durch drei Seiten, von der einfachsten Frage zur endgültigen:
 *   1. Stimmen die Zahlen überhaupt?
 *   2. Steckt der Rest noch im Versand?
 *   3. Was bleibt übrig — ignorieren oder neu anlegen?
 *
 * Unterwegs wird nichts geschrieben. Alle Eingaben werden gesammelt und am
 * Ende in einem Rutsch gespeichert: das Büro soll die Position erst zu Ende
 * denken können, statt nach jedem Teilschritt einen Zustand zu hinterlassen,
 * der halb entschieden ist.
 *
 * Bei Positionen mit Seriennummernpflicht entfällt die Mengen-Logik: dort ist
 * jede Nummer eine eigene Entscheidung, und es zählt, wo das Gerät laut System
 * gerade steckt.
 */
@Component({
  selector: 'app-expected-delivery-clarification-dialog',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './expected-delivery-clarification-dialog.component.html',
})
export class ExpectedDeliveryClarificationDialogComponent implements OnInit {
  private readonly service = inject(ExpectedDeliveryService);

  @Input({ required: true }) deliveryId!: number;
  /** Nur die Klärfälle dieser Buchung anzeigen. */
  @Input({ required: true }) receipt!: ExpectedDeliveryReceipt;

  @Output() closed = new EventEmitter<void>();
  /** Nach dem Speichern, damit die Seite ihre Zahlen neu lädt. */
  @Output() resolved = new EventEmitter<void>();

  protected readonly statusOptions = ARTICLE_STATUS_OPTIONS;

  protected readonly clarifications = signal<ExpectedDeliveryClarification[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  /** Auf welcher der drei Seiten der Dialog gerade steht. */
  protected readonly step = signal<1 | 2 | 3>(1);

  /**
   * Ist nichts mehr offen, wird der Dialog zum Nachweis: nur noch die Zahlen
   * und die getroffenen Entscheidungen, ohne Wizard. Sonst stünde man vor zwei
   * leeren Seiten und einem Speichern-Knopf, der nichts täte.
   */
  protected readonly isRecordOnly = computed(
    () => !this.isLoading() && this.clarifications().length > 0 && this.openClarifications().length === 0,
  );

  /** Korrekturfelder aus Schritt 1 — nur lokal, bis am Ende gespeichert wird. */
  protected readonly correctedOrdered = signal<number | null>(null);
  protected readonly correctedCounted = signal<number | null>(null);
  private originalOrdered: number | null = null;
  private originalCounted: number | null = null;

  /** Die gesammelten Entscheidungen je Klärfall. */
  protected readonly decisions = signal<Record<number, ClarificationDecision>>({});

  /** Lagerplatz-Auswahl für Defektteile, aus der Antwort des Backends. */
  protected readonly badPartBins = signal<ClarificationBadPartBins>({ storage_bins: [], suggested_id: null });
  /** Wohin die gewählten Defektteile gebucht werden. */
  protected readonly badPartBinId = signal<number | null>(null);

  protected readonly openClarifications = computed(() =>
    this.clarifications().filter(c => c.status === 'open'),
  );

  protected readonly resolvedClarifications = computed(() =>
    this.clarifications().filter(c => c.status === 'resolved'),
  );

  /** Ein Klärfall reicht für die Angaben zur Position — die gelten für alle. */
  protected readonly firstClarification = computed(() => this.clarifications()[0] ?? null);

  /**
   * Wie viel bei dieser Buchung geklärt werden musste. Bewusst die Summe über
   * alle Klärfälle: bei Seriennummern gibt es je Nummer einen mit Menge 1.
   */
  protected readonly totalToClarify = computed(() =>
    this.clarifications().reduce((sum, c) => sum + c.quantity, 0),
  );

  /** Was direkt umgebucht werden konnte. */
  protected readonly transferredCount = computed(() =>
    Math.max(0, this.receipt.quantity - this.totalToClarify()),
  );

  /** Was insgesamt noch offen ist. */
  protected readonly totalOpen = computed(() =>
    this.clarifications().reduce((sum, c) => sum + c.quantity_open, 0),
  );

  /** Bei Seriennummern hängt die Menge an konkreten Nummern — nicht korrigierbar. */
  protected readonly countedQuantityEditable = computed(
    () => this.firstClarification()?.article_template_sn !== true,
  );

  /**
   * Nur Klärfälle, zu denen es überhaupt etwas auszuwählen gibt. Der Rest wird
   * in Schritt 2 nicht gezeigt: eine Position ohne Kandidaten sieht sonst aus,
   * als ließe sich hier noch etwas einstellen.
   */
  protected readonly clarificationsWithCandidates = computed(() =>
    this.openClarifications().filter(c => c.candidates.length > 0),
  );

  /**
   * Die ausgewählten Defektteile. Sie dürfen nicht in den normalen Bestand —
   * deshalb braucht es für sie einen eigenen Lagerplatz, sobald welche dabei
   * sind.
   */
  protected readonly chosenBadParts = computed(() =>
    this.openClarifications().flatMap(clarification =>
      clarification.candidates.filter(
        candidate => candidate.bad_part && this.decisionFor(clarification.id).articleIds.includes(candidate.id),
      ),
    ),
  );

  protected readonly needsBadPartBin = computed(
    () => this.chosenBadParts().length > 0 && this.badPartBinId() === null,
  );

  /** Wie viel bei diesem Klärfall nach allen Eingaben unentschieden bleibt. */
  protected undecidedFor(clarification: ExpectedDeliveryClarification): number {
    const decision = this.decisionFor(clarification.id);
    return this.remainingFor(clarification) - decision.createQuantity - decision.ignoreQuantity;
  }

  /** Mehr verteilt als offen ist — das weist das Backend ab, also hier schon bremsen. */
  protected readonly hasOverbooking = computed(() =>
    this.openClarifications().some(clarification => this.undecidedFor(clarification) < 0),
  );

  /** Wie viele Artikel über alle Klärfälle hinweg zum Nachbuchen ausgewählt sind. */
  protected readonly totalChosen = computed(() =>
    this.openClarifications().reduce((sum, c) => sum + this.chosenCount(c.id), 0),
  );

  /** Wie viel nach der Auswahl aus Schritt 2 noch zu entscheiden bleibt. */
  protected readonly totalRemaining = computed(() =>
    this.openClarifications().reduce((sum, c) => sum + this.remainingFor(c), 0),
  );

  /**
   * Speichern ist erlaubt, sobald irgendetwas zu tun ist. Ein Rest ohne
   * gewählte Aktion blockiert nicht — er bleibt schlicht offen und lässt sich
   * später entscheiden, denn Teil-Auflösung ist ausdrücklich vorgesehen.
   */
  /** Speichern ist möglich, wenn es etwas zu tun gibt und nichts fehlt. */
  protected readonly canSave = computed(
    () => this.hasChanges() && !this.needsBadPartBin() && !this.hasOverbooking(),
  );

  protected readonly hasChanges = computed(() => {
    if (this.correctedOrdered() !== this.originalOrdered) return true;
    if (this.correctedCounted() !== this.originalCounted) return true;

    return this.openClarifications().some(clarification => {
      const decision = this.decisionFor(clarification.id);
      return this.chosenCount(clarification.id) > 0 || decision.createQuantity > 0 || decision.ignoreQuantity > 0;
    });
  });

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.service.getClarifications(this.deliveryId).subscribe({
      next: response => {
        const forThisReceipt = response.clarifications.filter(
          c => c.expected_delivery_items_receipt_id === this.receipt.id,
        );
        this.clarifications.set(forThisReceipt);
        this.seedDecisions(forThisReceipt);

        const bins = response.bad_part_bins?.[this.receipt.id] ?? { storage_bins: [], suggested_id: null };
        this.badPartBins.set(bins);
        this.badPartBinId.set(bins.suggested_id);

        this.originalOrdered = forThisReceipt[0]?.quantity_ordered ?? null;
        this.originalCounted = this.receipt.quantity;
        this.correctedOrdered.set(this.originalOrdered);
        this.correctedCounted.set(this.originalCounted);

        this.step.set(1);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Die offenen Positionen konnten nicht geladen werden.');
        this.isLoading.set(false);
      },
    });
  }

  private seedDecisions(clarifications: ExpectedDeliveryClarification[]): void {
    const decisions: Record<number, ClarificationDecision> = {};

    for (const clarification of clarifications) {
      decisions[clarification.id] = {
        articleIds: [],
        createQuantity: 0,
        ignoreQuantity: 0,
        createStatus: 'NEW',
        note: '',
      };
    }

    this.decisions.set(decisions);
  }

  // --- Navigation ----------------------------------------------------------

  protected next(): void {
    this.step.update(current => (current === 1 ? 2 : 3));
  }

  protected back(): void {
    this.step.update(current => (current === 3 ? 2 : 1));
  }

  // --- Auswahl in Schritt 2 ------------------------------------------------

  protected decisionFor(clarificationId: number): ClarificationDecision {
    return (
      this.decisions()[clarificationId] ?? {
        articleIds: [],
        createQuantity: 0,
        ignoreQuantity: 0,
        createStatus: 'NEW',
        note: '',
      }
    );
  }

  private updateDecision(clarificationId: number, change: Partial<ClarificationDecision>): void {
    const decisions = { ...this.decisions() };
    decisions[clarificationId] = { ...this.decisionFor(clarificationId), ...change };
    this.decisions.set(decisions);
  }

  protected isArticleSelected(clarificationId: number, articleId: number): boolean {
    return this.decisionFor(clarificationId).articleIds.includes(articleId);
  }

  /**
   * Zwei Gründe, warum nichts gewählt werden kann: der Artikel ist gesperrt
   * (etwa nachweislich unterwegs), oder es sind schon so viele gewählt wie
   * offen sind. Letzteres würde das Backend erst beim Speichern abweisen —
   * und dann stünde die Hälfte der Entscheidungen schon geschrieben da.
   * Abwählen bleibt immer möglich.
   */
  protected canSelect(
    clarification: ExpectedDeliveryClarification,
    candidate: ClarificationCandidate,
    alreadySelected: boolean,
  ): boolean {
    if (alreadySelected) return true;
    if (!candidate.selectable) return false;

    return this.chosenCount(clarification.id) < clarification.quantity_open;
  }

  protected toggleArticle(clarification: ExpectedDeliveryClarification, candidate: ClarificationCandidate): void {
    const articleId = candidate.id;
    const current = this.decisionFor(clarification.id).articleIds;
    const selected = current.includes(articleId);
    if (!this.canSelect(clarification, candidate, selected)) return;

    this.updateDecision(clarification.id, {
      articleIds: selected ? current.filter(id => id !== articleId) : [...current, articleId],
    });
  }

  /** Wie viele Artikel für diesen Klärfall ausgewählt sind. */
  protected chosenCount(clarificationId: number): number {
    return this.decisionFor(clarificationId).articleIds.length;
  }

  /** Was bei diesem Klärfall nach der Auswahl noch offen bleibt. */
  protected remainingFor(clarification: ExpectedDeliveryClarification): number {
    return Math.max(0, clarification.quantity_open - this.chosenCount(clarification.id));
  }

  // --- Eingaben in Schritt 3 ----------------------------------------------

  protected setCreateQuantity(clarification: ExpectedDeliveryClarification, value: string): void {
    this.updateDecision(clarification.id, { createQuantity: this.clampToRest(clarification, value) });
  }

  protected setIgnoreQuantity(clarification: ExpectedDeliveryClarification, value: string): void {
    this.updateDecision(clarification.id, { ignoreQuantity: this.clampToRest(clarification, value) });
  }

  private clampToRest(clarification: ExpectedDeliveryClarification, value: string): number {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;

    return Math.min(parsed, this.remainingFor(clarification));
  }

  /**
   * Bei einer Seriennummer geht es um ein einzelnes Gerät — dort ist die
   * Entscheidung ein Entweder-oder, keine Mengenverteilung. Intern laufen
   * beide Wege über dieselben Mengen, nur die Bedienung unterscheidet sich.
   */
  protected singleChoice(clarificationId: number): 'created' | 'ignored' | null {
    const decision = this.decisionFor(clarificationId);
    if (decision.createQuantity > 0) return 'created';
    if (decision.ignoreQuantity > 0) return 'ignored';

    return null;
  }

  protected toggleSingleChoice(clarification: ExpectedDeliveryClarification, choice: 'created' | 'ignored'): void {
    const next = this.singleChoice(clarification.id) === choice ? null : choice;
    const remaining = this.remainingFor(clarification);

    this.updateDecision(clarification.id, {
      createQuantity: next === 'created' ? remaining : 0,
      ignoreQuantity: next === 'ignored' ? remaining : 0,
    });
  }

  protected setBadPartBin(value: string): void {
    this.badPartBinId.set(value ? Number(value) : null);
  }

  protected setCreateStatus(clarificationId: number, value: string): void {
    this.updateDecision(clarificationId, { createStatus: value });
  }

  protected setNote(clarificationId: number, value: string): void {
    this.updateDecision(clarificationId, { note: value });
  }

  // --- Speichern -----------------------------------------------------------

  /**
   * Alles auf einmal, in fester Reihenfolge: erst die Mengenkorrekturen, dann
   * je Klärfall das Nachbuchen und zuletzt der Rest. Die Reihenfolge ist nicht
   * kosmetisch — das Backend rechnet die offene Menge nach jeder Aktion neu,
   * eine Rest-Entscheidung vor dem Nachbuchen würde zu viel abziehen.
   */
  protected save(): void {
    const tasks: Observable<unknown>[] = [];
    const clarification = this.firstClarification();

    const ordered = this.correctedOrdered();
    if (clarification && ordered !== null && ordered !== this.originalOrdered) {
      tasks.push(this.service.updateItemQuantity(this.deliveryId, clarification.expected_delivery_item_id, ordered));
    }

    const counted = this.correctedCounted();
    if (counted !== null && counted !== this.originalCounted) {
      tasks.push(this.service.updateReceiptQuantity(this.deliveryId, this.receipt.id, counted));
    }

    for (const open of this.openClarifications()) {
      const decision = this.decisionFor(open.id);

      // Nach Herkunft gruppieren: Artikel aus einem Lieferschein werden
      // nachgebucht und halten den Lieferschein als Nachweis fest, alle
      // anderen sind eine Umbuchung von ihrem Standort.
      for (const [key, articleIds] of Object.entries(this.groupByOrigin(open, decision.articleIds))) {
        if (articleIds.length === 0) continue;

        const deliveryNoteId = key === 'transferred' ? undefined : Number(key);
        tasks.push(
          this.resolveRequest(open, deliveryNoteId === undefined ? 'transferred' : 'booked_back', {
            quantity: articleIds.length,
            articleIds,
            deliveryNoteId,
          }),
        );
      }

      // Der Rest lässt sich aufteilen: ein Teil neu anlegen, ein Teil
      // ignorieren, ein Teil offen lassen. Neu anlegen zuerst, damit bei einem
      // Fehler die folgenreichere Entscheidung nicht halb geschrieben ist.
      if (decision.createQuantity > 0) {
        tasks.push(
          this.resolveRequest(open, 'created', {
            quantity: decision.createQuantity,
            articleStatus: decision.createStatus,
          }),
        );
      }

      if (decision.ignoreQuantity > 0) {
        tasks.push(this.resolveRequest(open, 'ignored', { quantity: decision.ignoreQuantity }));
      }
    }

    if (tasks.length === 0) {
      this.close();
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);

    concat(...tasks)
      .pipe(defaultIfEmpty(null), last())
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.resolved.emit();
          this.close();
        },
        error: err => {
          this.isSaving.set(false);
          const message = (err as { error?: { error?: string } })?.error?.error;
          this.errorMessage.set(
            message || 'Die Entscheidung konnte nicht gespeichert werden. Bereits gespeicherte Schritte bleiben erhalten.',
          );
          this.resolved.emit();
          this.load();
        },
      });
  }

  /** Gewählte Artikel nach ihrem Lieferschein bündeln; ohne Lieferschein unter 'transferred'. */
  private groupByOrigin(
    clarification: ExpectedDeliveryClarification,
    articleIds: number[],
  ): Record<string, number[]> {
    const groups: Record<string, number[]> = {};

    for (const articleId of articleIds) {
      const candidate = clarification.candidates.find(c => c.id === articleId);
      const key = candidate?.delivery_note_id ? String(candidate.delivery_note_id) : 'transferred';
      groups[key] = [...(groups[key] ?? []), articleId];
    }

    return groups;
  }

  private resolveRequest(
    clarification: ExpectedDeliveryClarification,
    action: ClarificationAction,
    options: { quantity: number; articleIds?: number[]; articleStatus?: string; deliveryNoteId?: number },
  ): Observable<unknown> {
    return this.service.resolveClarification(this.deliveryId, clarification.id, {
      action_type: action,
      quantity: options.quantity,
      article_ids: options.articleIds,
      article_status: options.articleStatus,
      delivery_note_id: options.deliveryNoteId,
      bad_part_storage_bin_id: this.badPartBinId() ?? undefined,
      note: this.decisionFor(clarification.id).note || undefined,
    }) as Observable<unknown>;
  }

  // --- Anzeige-Helfer ------------------------------------------------------

  protected articleUrl(articleId: number): string {
    return `/a/artikel/${articleId}`;
  }

  protected actionLabel(action: ClarificationAction): string {
    switch (action) {
      case 'booked_back':
        return 'Nachgebucht';
      case 'transferred':
        return 'Umgebucht';
      case 'created':
        return 'Neu angelegt';
      default:
        return 'Ignoriert';
    }
  }

  protected locationLabel(location: string): string {
    switch (location) {
      case 'WAREHOUSE':
        return 'Im Lager';
      case 'SHIPPING':
        return 'Noch im Versand';
      case 'CONSUMED':
        return 'Als verbraucht erfasst';
      default:
        return location;
    }
  }

  protected close(): void {
    this.closed.emit();
  }
}
